/****************************************************
 * Module Name:  AddTenderStoneModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Button, TextField, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import Constants from "../../../utility/constants";
import { Modal } from "react-bootstrap";
import logo from '../../../images/pansuriya.jpg'
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import renderCellExpand from "../../common/dataGridCellExpand";
import EditIcon from '@mui/icons-material/Edit';
import { LoadingButton } from "@mui/lab";
import { BackupTable } from "@mui/icons-material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CommonSnackbar from "../../common/commonSnackBar";
import Papa from 'papaparse';
import SaveIcon from '@mui/icons-material/Save';
import Utility from "../../../utility/utility";

const AddTenderStoneModal = ({ show, handleClose, tenderlot, handleSave, loader }) => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [noOfStones, setNoOfStones] = useState(tenderlot.pcs);
    const [row, setRow] = useState([]);
    const [totalSize, setTotalSize] = useState(0);
    const [totalPercentage, setTotalPercentage] = useState(0);
    const [totalPieces, setTotalPieces] = useState(0);
    const [alertSnackbar, setAlertSnackbar] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [uploadLoader, setUploadLoader] = useState(false);
    const [uploadSuccessSnackbar, setUploadSuccessSnackbar] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [validateStoneMsg, setValidateStoneMsg] = useState('');
    const [snackbarVariant, setSnackbarVariant] = useState();
    const tenderLotSize = (tenderlot.carats).toFixed(2);
    const tenderLotPieces = tenderlot.pcs;

    const updateLastRecord = (updatedRows, totalSize, totalPercentage) => {
        let completedcount = 0;

        updatedRows.forEach(irow => {
            if (irow.size !== 0 && irow.stonesizepercent !== 0) {
                completedcount++;
            }
        })

        if (completedcount === updatedRows.length - 1) {
            let tempData = updatedRows.map(irow => {
                if (irow.size === 0 && irow.stonesizepercent === 0) {

                    irow.stonesizepercent = (100 - totalPercentage).toFixed(2);
                    irow.size = (tenderLotSize - totalSize).toFixed(2);
                }
                return irow;
            })

            setRow(tempData);

            let sizeT = 0, percentageT = 0;
            tempData.forEach(iRow => {
                sizeT += parseFloat(iRow.size);
                percentageT += parseFloat(iRow.stonesizepercent);
            })

            setTotalSize(sizeT.toFixed(2));
            setTotalPercentage(percentageT.toFixed(2));
        }
    }

    const columns = [
        {
            field: "id",
            headerName: "SR NO.",
            headerAlign: "center",
            align: "center",
            width: 160,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tenderlotid",
            headerName: "TENDER LOT ID",
            headerAlign: "center",
            align: "center",
            width: 160,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "lotcode",
            headerName: "LOT CODE",
            headerAlign: "center",
            align: "center",
            width: 160,
            renderCell: renderCellExpand,
        },
        {
            field: "pieces",
            headerName: "PIECES",
            headerAlign: "center",
            align: "center",
            width: 160,
            type: "number",
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: "70%" }}>
                    <span style={{ width: "83%", textAlign: "end" }}>{params.row.pieces}</span>
                    {<EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', marginRight: -16 }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
        },
        {
            field: "size",
            headerName: "STONE SIZE",
            headerAlign: "center",
            align: "center",
            width: 160,
            type: "number",
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: "70%" }}>
                    <span style={{ width: "83%", textAlign: "end" }}>{params.row.size}</span>
                    {<EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', marginRight: -16 }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
        },
        {
            field: "stonesizepercent",
            headerName: "STONE SIZE PERCENT",
            headerAlign: "center",
            align: "center",
            width: 160,
            type: "number",
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: "70%" }}>
                    <span style={{ width: "83%", textAlign: "end" }}>{params.row.stonesizepercent}</span>
                    {<EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', marginRight: -16 }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
        },
    ];

    const addTenderStone = () => {
        const numObjects = parseInt(noOfStones, 10); // Convert noOfStones to an integer
        if (numObjects > 0) {
            const newArray = Array.from({ length: numObjects }, (_, index) => ({
                id: index + 1,
                tenderlotid: tenderlot.tenderlotid,
                lotcode: tenderlot.lotcode,
                size: 0,
                stonesizepercent: 0,
                pieces: 0,
            }));
            setRow(newArray);
        }
    }

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];

        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.id,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const processRowUpdate = (newRow, oldRow) => {
        const changedFeilds = getChangedFields(newRow, oldRow);
        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];
            if (value <= 0) return { ...oldRow };

            let updatedRow;
            const updatedListData = row.map((iStone) => {
                if (iStone.id === id && value) {
                    if (field === "size") {
                        updatedRow = {
                            ...iStone,
                            [field]: value.toFixed(2),
                            stonesizepercent: ((value / tenderLotSize) * 100).toFixed(2)
                        }
                    } else if (field === "stonesizepercent") {
                        updatedRow = {
                            ...iStone,
                            [field]: value.toFixed(2),
                            size: ((value / 100) * tenderLotSize).toFixed(2)
                        }
                    } else if (field === "pieces" && Number.isInteger(value)) {
                        updatedRow = {
                            ...iStone,
                            [field]: value
                        }
                    } else {
                        setShowSnackbar(true);
                        setSnackbarMessage("Invalid Data");
                        setSnackbarVariant("warning");
                    }
                    return updatedRow;
                }
                return iStone;
            })

            let totalSize = 0, totalPercentage = 0, totalPieces = 0;
            updatedListData.forEach(iRow => {
                totalSize += parseFloat(iRow.size);
                totalPercentage += parseFloat(iRow.stonesizepercent);
                totalPieces += iRow.pieces;
            })

            setTotalSize(totalSize.toFixed(2));
            setTotalPercentage(totalPercentage.toFixed(2));
            setTotalPieces(totalPieces);
            setRow(updatedListData);

            updateLastRecord(updatedListData, totalSize, totalPercentage)
            return updatedRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newRow };
        }
    };

    const handleUpload = (file) => {
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    if (result.data.length <= 0) {
                        setAlertSnackbar(true);
                        setAlertMessage("No Stones Found");
                        return;
                    }

                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['id', 'stone size percent', 'size', 'pieces'];
                    if (requiredColumns.length === headersFromCSV.length) {
                        let updatedRow = [], ids = new Set();;
                        result.data.forEach(record => {
                            row.forEach(row => {
                                if (row.id === parseInt(record['id'])) {
                                    let iRow = {};
                                    if (record['stone size percent'] && parseFloat(record['stone size percent']) > 0 && record['pieces'] && parseFloat(record['pieces']) > 0) {
                                        iRow = {
                                            ...row,
                                            stonesizepercent: parseFloat(record['stone size percent']).toFixed(2),
                                            size: ((parseFloat(record['stone size percent']) / 100) * tenderLotSize).toFixed(2),
                                            pieces: parseInt(record['pieces'])
                                        }
                                        updatedRow.push(iRow);
                                        ids.add(row.id);
                                    } else if (record['size'] && parseFloat(record['size']) > 0 && record['pieces'] && parseFloat(record['pieces']) > 0) {
                                        iRow = {
                                            ...row,
                                            size: parseFloat(record['size']).toFixed(2),
                                            stonesizepercent: ((parseFloat(record['size']) / tenderLotSize) * 100).toFixed(2),
                                            pieces: parseInt(record['pieces'])
                                        }
                                        updatedRow.push(iRow);
                                        ids.add(row.id);
                                    }
                                }
                            });
                        });

                        //to add not updated stones
                        row.forEach(iRow => {
                            if (!ids.has(iRow.id)) {
                                updatedRow.push(iRow);
                            }
                        });

                        let sizeT = 0, percentageT = 0, piecesT = 0;
                        updatedRow.forEach(iRow => {
                            sizeT += parseFloat(iRow.size);
                            percentageT += parseFloat(iRow.stonesizepercent);
                            piecesT += iRow.pieces;
                        })

                        setTotalSize(sizeT.toFixed(2));
                        setTotalPercentage(percentageT.toFixed(2));
                        setTotalPieces(piecesT);

                        setRow(updatedRow);
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(true);
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(true);
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setAlertSnackbar(true);
                }
            });
        };

        reader.readAsText(file);
    };

    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const handleCreateStones = () => {
        let flagRow = row.filter(iRow => !iRow.pieces || !iRow.size);
        if (flagRow.length > 0 || totalSize !== tenderLotSize || totalPieces !== tenderLotPieces) {
            setShowSnackbar(true);
            setSnackbarMessage("Total stone size or Total stone pieces are not equal to Tender Lot Size or Pieces respectively.");
            setSnackbarVariant("warning");
        } else {
            let data = [];

            row.forEach(iData => {

                let item = {
                    tenderlotid: iData.tenderlotid,
                    size: parseFloat(iData.size),
                    stonesizepercent: parseFloat(iData.stonesizepercent),
                    pcs: iData.pieces,
                }
                data.push(item);
            })

            handleSave(data);
        }
    }

    return (
        <Box>
            <Modal
                show={show}
                onHide={handleClose}
                enforceFocus={true}
                backdrop="static"
                size="xl"
            >
                <CommonSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" : snackbarVariant === "error" ? "Error" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />

                <CommonSnackbar
                    show={alertSnackbar}
                    message={alertMessage}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={"Failed"}
                    handleClose={() => setAlertSnackbar(false)}
                />

                {row ? (
                    <CommonSnackbar
                        show={uploadSuccessSnackbar}
                        message={
                            row.length !== 0
                                ? `File loaded Successfully`
                                : `File could not be loaded`
                        }
                        variant={row.length !== 0 ? "success" : "error"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={row.length !== 0 ? "Success" : "Error"}
                        handleClose={() => setUploadSuccessSnackbar(false)}
                    />
                ) : null}

                <Modal.Header closeButton>
                    <Modal.Title>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            <Box sx={{ ml: 2 }}>
                                <Typography variant="h5">ADD TENDER STONES</Typography>
                                <Typography variant="body2">
                                    <strong>TENDER LOT SIZE:</strong> {tenderLotSize} &nbsp;&nbsp; | &nbsp;&nbsp;
                                    <strong>TOTAL LOT PIECES:</strong> {tenderLotPieces}
                                </Typography>
                            </Box>
                        </Box>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            margin: "15px 0",
                            "& .MuiOutlinedInput-input": {
                                padding: "9px !important"
                            },
                            "& .MuiInputLabel-root": {
                                top: "-7px !important"
                            }
                        }}>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                width: "50%"
                            }}>
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%"
                                }}>
                                    <Typography sx={{ width: "20%", margin: "0px" }}>No. of Stones
                                        <sup style={{ color: "red", marginLeft: "4px" }}>*</sup>
                                    </Typography>

                                    <div style={{ display: "flex", marginLeft: "10px", flexDirection: "column" }}>
                                        <div style={{ flexDirection: "row" }}>
                                            <TextField
                                                sx={{ margin: 0 }}
                                                margin="normal"
                                                label="No. of Stones (required)"
                                                placeholder="Enter No. of Stones"
                                                type="number"
                                                value={noOfStones}
                                                onChange={(e) => {
                                                    // Only allow setting positive integers or empty string
                                                    const value = e.target.value;
                                                    if (/^\d*$/.test(value)) {  // Regex to ensure only positive integers or empty string
                                                        setNoOfStones(value);
                                                    }
                                                    setValidateStoneMsg(Utility.CheckIsPresent(value) && parseInt(value) > tenderlot.pcs
                                                        ? `Number of pieces cannot be greater than ${tenderlot.pcs}`
                                                        : "")
                                                }}
                                                onKeyDown={(e) => {
                                                    // Prevent any non-numeric key press, including backspace, arrows, etc.
                                                    if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Tab") {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            {/* ------------ Apply Button ---------- */}
                                            <Button
                                                variant="contained"
                                                onClick={addTenderStone}
                                                sx={{
                                                    height: "40px",
                                                    marginBottom: "3px",
                                                    minWidth: "80px",
                                                    marginLeft: "20px"
                                                }}
                                                disabled={validateStoneMsg}
                                            >
                                                Apply
                                            </Button>
                                        </div>
                                        <text style={{ color: "red", marginLeft: "1px", fontSize: "12px" }}>
                                            {validateStoneMsg || " "}
                                        </text>
                                    </div>
                                </Box>
                            </Box>
                        </Box>

                        {/* ------------ Table ---------- */}
                        {row.length > 0 ? (
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    width: "100%",
                                    position: "relative",
                                    "& .MuiTablePagination-selectLabel": {
                                        marginTop: "15px",
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        marginTop: "15px",
                                    },
                                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-cc8tf1": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .lavenderCell": {
                                        color: "#080808",
                                        backgroundColor: "#D8BFD8",
                                    },
                                    height: "auto",
                                    "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                                        minHeight: "30px"
                                    },
                                    "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                                        margin: 0
                                    }
                                }}
                            >
                                <DataGridPro
                                    disableRowSelectionOnClick={true}
                                    density="compact"
                                    columns={columns}
                                    rows={row}
                                    // loading={tenderLotLoader}
                                    sx={{
                                        '& .MuiDataGrid-columnHeader': {
                                            color: '#fff',
                                            backgroundColor: 'grey',
                                        },
                                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                            py: "4px",
                                        },
                                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                            py: "12px",
                                        },
                                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                            py: "18px",
                                        },

                                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                        maxHeight: "67vh"
                                    }}
                                    slots={{ toolbar: GridToolbar }}
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 },
                                        },
                                    }}
                                    getRowId={(row) => row.id}
                                    initialState={{
                                        pagination: {
                                            paginationModel: Constants.pageSize,
                                        },
                                    }}
                                    pageSizeOptions={Constants.pageSizeOptions}
                                    pagination
                                    processRowUpdate={processRowUpdate}
                                    onProcessRowUpdateError={handleProcessRowUpdateError}
                                    apiRef={apiRef}
                                    onCellClick={handleCellClick}
                                    editMode="cell"
                                />

                                <Box
                                    style={{
                                        position: "absolute",
                                        top: 6,
                                        right: "265px",
                                        width: "13%"
                                    }}
                                >
                                    <Box sx={{ display: "flex" }}>
                                        <Tooltip title="Download Sample Add Tender Stone">
                                            <a
                                                href="/demo_excel/SampleAddTenderStoneUpload.csv"
                                                download="SampleAddTenderStoneUpload.csv"
                                                style={{ color: "red", marginRight: "10px", marginLeft: "10px" }}
                                            >
                                                <BackupTable size={25} />
                                            </a>
                                        </Tooltip>

                                        <Tooltip title="Upload Tender Stone">
                                            <Box sx={{
                                                "& .Mui-disabled": {
                                                    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                                    color: "rgba(0, 0, 0, 0.26) !important"
                                                }
                                            }}>
                                                <LoadingButton
                                                    color="info"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ ml: "3px" }}
                                                    component="label"
                                                    loading={uploadLoader}
                                                    loadingPosition="start"
                                                    startIcon={<UploadFileIcon size="large" />}
                                                >
                                                    Upload
                                                    <input
                                                        id="file-input"
                                                        hidden
                                                        accept=".csv"
                                                        type="file"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                setUploadLoader(true);
                                                                handleUpload(file);
                                                            }
                                                        }}
                                                        onClick={(e) => {
                                                            handleClear()
                                                        }}
                                                    />
                                                </LoadingButton>
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                <Box
                                    style={{
                                        position: "absolute",
                                        bottom: 9,
                                        left: "10px",
                                        display: "flex",
                                    }}
                                >
                                    <Typography>Total Percentage : {totalPercentage}&nbsp;&nbsp;|&nbsp;&nbsp;</Typography>
                                    <Typography>Total Size : {totalSize}&nbsp;&nbsp;|&nbsp;&nbsp; </Typography>
                                    <Typography>Total Pieces : {totalPieces}</Typography>
                                </Box>
                            </Box>
                        ) : null}
                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton
                        sx={{ marginRight: "10px" }}
                        variant="contained"
                        color="success"
                        onClick={handleCreateStones}
                        loading={loader}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                        disabled={row.length === 0}
                    >
                        Create Tender Stones
                    </LoadingButton>
                    <Button variant="contained" color="error" onClick={handleClose}> Close </Button>
                </Modal.Footer>
            </Modal >
        </Box>
    )
}

export default AddTenderStoneModal;